import { formatSecondsToDurationLabel } from "@memorang/helpers/common";
import { getReportSkills } from "@memorang/helpers/report";
import type { MixedItemTag } from "@memorang/types";
import { Trans, useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import { PteOverallScoreCard } from "./PteOverallScoreCard";
import { SessionReportScoreDonut } from "./SessionReportScoreDonut";
import { getSummaryMessage } from "./helpers";

type Props = {
	tags: MixedItemTag[];
	topics: string[];
	numItems: number;
	timeElapsed: number;
	overallScore?: {
		score: number;
		maxScore: number;
	};
	showPteOverallScoreCard?: boolean;
};

export const SessionReportScoreSection = ({
	tags,
	topics,
	numItems,
	timeElapsed,
	overallScore,
	showPteOverallScoreCard = false,
}: Props) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const { t, i18n } = useTranslation();

	const summaryMessage = getSummaryMessage({
		tags,
		topics,
		numItems,
		t,
		locale: i18n.language,
	});

	const skillTags = getReportSkills(tags, topics);
	const showPteCard = showPteOverallScoreCard && Boolean(overallScore);
	const isSingleScore =
		showPteCard || Boolean(overallScore) || skillTags.length === 1;

	const overallScoreView = overallScore ? (
		showPteOverallScoreCard ? (
			<PteOverallScoreCard
				label={t("session.results.summary.overallScore")}
				score={overallScore.score}
			/>
		) : (
			<SessionReportScoreDonut
				score={Math.floor(overallScore.score)}
				maxScore={overallScore.maxScore}
				progress={overallScore.score / overallScore.maxScore}
				showSurfaceHalo={Boolean(isSingleScore)}
			/>
		)
	) : null;

	return (
		<Box gap={16} width="100%">
			{overallScore || skillTags.length > 0 ? (
				<Box
					flexDirection="row"
					justifyContent="center"
					alignItems="flex-start"
					gap={isMobile ? 24 : 62}
					width="100%"
					marginTop={isSingleScore ? -72 : 0}
				>
					{overallScoreView ??
						skillTags.map((skill) => {
							const score = skill.numTotalPoints
								? (skill.numPointsEarned ?? 0)
								: (skill.averageScore ?? 0);
							const maxScore = skill.numTotalPoints || 100;
							return (
								<SessionReportScoreDonut
									key={skill.id}
									label={skillTags.length === 1 ? undefined : skill.title}
									score={score}
									maxScore={maxScore}
									progress={score / maxScore}
									showSurfaceHalo={Boolean(isSingleScore)}
								/>
							);
						})}
				</Box>
			) : null}
			<Text
				variant="bodyMedium"
				style={{ color: theme.colors.onSurface, textAlign: "center" }}
			>
				<Trans
					i18nKey={summaryMessage.key}
					shouldUnescape
					tOptions={{ interpolation: { escapeValue: true } }}
					values={{
						...summaryMessage.values,
						timeTaken: formatSecondsToDurationLabel(timeElapsed),
					}}
					components={{
						bold: (
							<Text
								variant="bodyMedium"
								style={{
									color: theme.colors.onSurface,
									fontWeight: "700",
								}}
							>
								{""}
							</Text>
						),
					}}
				/>
			</Text>
		</Box>
	);
};
